import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authAction, disclaimerAction } from "@redux/modules";

/**
 * PrivateRoute = authenticated + (componentName aktif mi?) + Sorumluluk Reddi onaylı mı?
 *
 * Disclaimer gate akışı:
 *   - isAuthenticated true olduğunda, disclaimer state'i henüz initialized değilse
 *     /employee/disclaimer/{employeeFirmId} çağrılır.
 *   - required === true ise (aktif disclaimer var ve geçerli onayı yok) kullanıcı
 *     /sorumluluk-reddi-beyani'ye yönlendirilir; URL'den manuel başka sayfaya
 *     geçmesi engellenir.
 *   - Disclaimer sayfasının kendisi bu yönlendirmeden muaftır (aksi halde sonsuz döngü).
 *   - initialized=false iken (fetch devam ederken) hiçbir route render edilmez,
 *     null döndürülür — böylece korunan içerik bir an bile görünmez.
 */
const DISCLAIMER_PATH = "/sorumluluk-reddi-beyani";

const PrivateRoute = ({
  children,
  isAuthenticated,
  fallback,
  componentName,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const activeComponents = useSelector((store) => store.auth.activeComponents);
  const authUser = useSelector((store) => store.auth.authUser);
  const employeeFirmId = authUser?.employeeFirm?.id;

  const disclaimerInitialized = useSelector(
    (store) => store.disclaimer.initialized
  );
  const disclaimerLoading = useSelector(
    (store) => store.disclaimer.isLoading
  );
  const disclaimerRequired = useSelector(
    (store) => store.disclaimer.required
  );

  // Active component listesi yoksa çek (mevcut davranış)
  React.useEffect(() => {
    if (!activeComponents && isAuthenticated) {
      dispatch(authAction.getActiveComponents());
    }
  }, [dispatch, isAuthenticated]);

  // Disclaimer durumu henüz çekilmediyse ve auth varsa fetch et.
  // Reload edildiğinde (redux-persist whitelist'te disclaimer YOK)
  // initialized=false ile başlayacağı için tekrar fetch olur.
  React.useEffect(() => {
    if (
      isAuthenticated &&
      employeeFirmId &&
      !disclaimerInitialized &&
      !disclaimerLoading
    ) {
      dispatch(
        disclaimerAction.getDisclaimerByEmployeeFirmId(employeeFirmId)
      );
    }
  }, [
    dispatch,
    isAuthenticated,
    employeeFirmId,
    disclaimerInitialized,
    disclaimerLoading,
  ]);

  // 1) Not authenticated → /login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 2) Disclaimer durumu daha bilinmiyor → loading; içeriği render etme
  //    (kısa bir an için bile korumalı sayfa görünmesin)
  if (!disclaimerInitialized) {
    return null;
  }

  // 3) Disclaimer zorunlu ve şu an Disclaimer sayfasında değiliz → kilitle
  if (disclaimerRequired === true && location.pathname !== DISCLAIMER_PATH) {
    // Kullanıcının gitmek istediği sayfayı sakla; onayladıktan sonra
    // Disclaimer sayfası bu hedefe geri yönlendirir.
    if (
      !sessionStorage.getItem("postDisclaimerRedirect") &&
      location.pathname &&
      location.pathname !== DISCLAIMER_PATH &&
      location.pathname !== "/login"
    ) {
      sessionStorage.setItem("postDisclaimerRedirect", location.pathname);
    }
    return <Navigate to={DISCLAIMER_PATH} replace />;
  }

  // 4) Component aktif değilse → /home
  const isComponentActive = () => {
    if (!componentName) return true;
    if (!activeComponents || !activeComponents.length) return false;
    return activeComponents.some(
      (item) => item.active && item.componentName === componentName
    );
  };

  if (!isComponentActive()) {
    console.log(
      `Component "${componentName}" is not active, redirecting to home`
    );
    return <Navigate to="/home" replace />;
  }

  return children;
};

export default PrivateRoute;
